'use client';

import { Systemstreifen } from '@kit/ui/systemstreifen';

import { MarketingImage } from '~/(marketing)/_components/ui/marketing-image';
import { Eur } from '~/(marketing)/_components/ui/price';

import { type Locale, authHref } from '../../../_lib/marketing-routes';
import {
  PORTAL_LOGOS_BASIS,
  PORTAL_LOGOS_PLUS,
} from '../../../_lib/portal-logos';
import type { PricingSelectorLabels, PricingTierContent } from '../../_content';
import { usePricingDuration } from './pricing-context';
import {
  DURATIONS,
  DURATION_DAYS,
  type Duration,
  type PreisMatrix,
  durationLabel,
} from './pricing-data';

type Delta = { text: string; cls: string };

function buildDelta(
  matrix: PreisMatrix,
  activeDuration: Duration,
  otherDuration: Duration,
  labels: PricingSelectorLabels,
): Delta {
  const activePerDay =
    matrix.basis[activeDuration] / DURATION_DAYS[activeDuration];
  const otherPerDay =
    matrix.basis[otherDuration] / DURATION_DAYS[otherDuration];
  const delta = ((otherPerDay - activePerDay) / activePerDay) * 100;
  const rounded = Math.round(Math.abs(delta));
  if (delta < -0.5)
    return {
      text: labels.subCheaper.replace('{pct}', String(rounded)),
      cls: 'duration-sub--cheaper',
    };
  if (delta > 0.5)
    return {
      text: labels.subCostlier.replace('{pct}', String(rounded)),
      cls: 'duration-sub--costlier',
    };
  return { text: labels.subSameDay, cls: '' };
}

function subLabel(
  matrix: PreisMatrix,
  duration: Duration,
  active: Duration,
  labels: PricingSelectorLabels,
): Delta {
  if (duration === active) {
    return { text: labels.subActive, cls: 'duration-sub--active' };
  }
  return buildDelta(matrix, active, duration, labels);
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

interface TierCardProps {
  paket: 'basis' | 'plus';
  tier: PricingTierContent;
  price: number;
  durationText: string;
  numberLocale: string;
  featured: boolean;
  /** Abzeichen „Empfohlen" (F4) — gesetzt am empfohlenen Paket. */
  badgeEmpfohlen?: string;
  logos: { src: string; alt: string }[];
  locale: Locale;
}

function TierCard({
  paket,
  tier,
  price,
  durationText,
  numberLocale,
  featured,
  badgeEmpfohlen,
  logos,
  locale,
}: TierCardProps) {
  const fmtAmount = price.toLocaleString(numberLocale);

  return (
    <article
      className={
        featured ? 'pricing-card pricing-card--featured' : 'pricing-card'
      }
      data-tier={paket}
    >
      {/*
       * Kopf = erste Rasterzeile: Abzeichen-Zeile über dem Paketnamen. Die
       * Abzeichen stehen im Fluss statt absolut, damit „Empfohlen“ und die
       * Garantie bei schmaler Karte umbrechen statt sich zu überdecken.
       */}
      <div className="tier-kopf">
        <div className="pricing-card-badges">
          {badgeEmpfohlen ? (
            <span className="pricing-card-badge-popular">{badgeEmpfohlen}</span>
          ) : null}
          <span className="pricing-card-badge-guarantee" aria-hidden="true">
            {tier.badgeGuarantee}
          </span>
        </div>
        <p className="tier-name">{tier.tierName}</p>
      </div>
      <p className="tier-price">
        <span className="tier-price-amount">
          <Eur>{fmtAmount}</Eur>
        </span>
        <span className="tier-price-duration">{durationText}</span>
      </p>
      <ul className="tier-features">
        {tier.features.map((feature) => (
          <li key={feature}>
            <CheckIcon />
            {feature}
          </li>
        ))}
      </ul>
      <div className="tier-portals">
        <p className="tier-portals-label">{tier.portalsLabel}</p>
        <div className="tier-portals-logos">
          {logos.map((logo) => (
            <MarketingImage
              key={logo.src}
              src={logo.src}
              alt={logo.alt}
              loading="lazy"
            />
          ))}
        </div>
      </div>
      <div className="tier-cta-wrap">
        <a
          href={authHref('signUp', locale)}
          // R-32: Vermillion nur am Hero- und Schlussknopf der Seite; das
          // hervorgehobene Paket zeigt sich am Ring und am Abzeichen.
          className="btn btn-secondary"
        >
          {tier.ctaLabel}
        </a>
        <p className="pricing-card-footer-microcopy">{tier.footerMicrocopy}</p>
      </div>
    </article>
  );
}

export function PricingSelector({
  labels,
  locale,
}: {
  labels: PricingSelectorLabels;
  locale: Locale;
}) {
  const { duration, setDuration, matrix, empfohlen } = usePricingDuration();

  const durationText = durationLabel(duration, labels);

  /*
   * Ohne Preisstand KEIN Betrag (Spec preise-aus-einer-quelle §4.3) — eine
   * Rückfallzahl im Code gibt es nicht. Der Streifen sagt es, statt eine
   * leere Karte oder eine erfundene Zahl zu zeigen.
   */
  if (!matrix) {
    return (
      <div data-test="preise-nicht-abrufbar">
        <Systemstreifen
          zustand={labels.preiseNichtAbrufbar.zustand}
          folge={labels.preiseNichtAbrufbar.folge}
          ausweg={{
            label: labels.preiseNichtAbrufbar.ausweg,
            href: '#selector',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className="duration-control">
        <div
          className="duration-toggle"
          role="tablist"
          aria-label={labels.durationToggleAria}
        >
          {DURATIONS.map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              aria-pressed={duration === d}
              onClick={() => setDuration(d)}
            >
              {durationLabel(d, labels)}
            </button>
          ))}
        </div>
        <div className="duration-sublabels" aria-live="polite">
          {DURATIONS.map((d) => {
            const sub = subLabel(matrix, d, duration, labels);
            return (
              <span
                key={d}
                className={`duration-sub${sub.cls ? ' ' + sub.cls : ''}`}
              >
                {sub.text}
              </span>
            );
          })}
        </div>
      </div>

      {/*
       * Die Karten teilen sich die Zeilen des Rasters (Subgrid in preise.css):
       * Paketname, Preis, Merkmale, Portale, Knopf mit Fußsatz.
       */}
      <div className="pricing-grid">
        <TierCard
          tier={labels.basis}
          price={matrix.basis[duration]}
          durationText={durationText}
          numberLocale={labels.numberLocale}
          paket="basis"
          featured={empfohlen === 'basis'}
          badgeEmpfohlen={
            empfohlen === 'basis' ? labels.badgeEmpfohlen : undefined
          }
          logos={PORTAL_LOGOS_BASIS}
          locale={locale}
        />
        <TierCard
          tier={labels.plus}
          price={matrix.plus[duration]}
          durationText={durationText}
          numberLocale={labels.numberLocale}
          paket="plus"
          featured={empfohlen === 'plus'}
          badgeEmpfohlen={
            empfohlen === 'plus' ? labels.badgeEmpfohlen : undefined
          }
          logos={PORTAL_LOGOS_PLUS}
          locale={locale}
        />
      </div>
    </>
  );
}
